import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import footer_logo from '../Assets/logo_big.png'
import instagram_icon from '../Assets/instagram_icon.png'
import pintrest_icon from '../Assets/pintester_icon.png'
import whatsapp_icon from '../Assets/whatsapp_icon.png'
import { getCategories } from '../../common/api/api'
import { getActiveCategories } from '../../common/categoryTree'

const FALLBACK_CATEGORIES = [
  { label: 'Laptops', to: '/category/laptops' },
  { label: 'Mobiles', to: '/category/mobiles' },
  { label: 'Accessories', to: '/category/accessories' },
]

const Footer = () => {
  const [dynamicCategories, setDynamicCategories] = useState(FALLBACK_CATEGORIES)

  const quickLinks = [
    { label: 'Home', to: '/' },
    { label: 'Cart', to: '/cart' },
    { label: 'My Profile', to: '/profile' },
  ];

  const socialLinks = [
    { img: instagram_icon, label: 'Instagram', href: '#', hoverColor: '#e1306c' },
    { img: pintrest_icon,  label: 'Pinterest',  href: '#', hoverColor: '#e60023' },
    { img: whatsapp_icon,  label: 'WhatsApp',   href: '#', hoverColor: '#25d366' },
  ];

  const payments = [
    { label: 'Visa',       color: '#1a56db' },
    { label: 'MasterCard', color: '#eb001b' },
    { label: 'JazzCash',   color: '#e91e8c' },
    { label: 'EasyPaisa',  color: '#4caf50' },
    { label: 'COD',        color: '#f59e0b' },
  ];

  const policies = ['Privacy Policy', 'Terms of Service', 'Refund Policy', 'Shipping Info'];

  useEffect(() => {
    const loadCategories = () => {
      getCategories()
        .then((data) => {
          const active = getActiveCategories(data);
          if (!active.length) return;

          const mapped = active
            .flatMap((cat) => {
              const base = [{ label: cat.name, to: `/category/${cat.slug}` }];
              const subLinks = (cat.subcategories || [])
                .filter((sub) => sub.active !== false)
                .slice(0, 2)
                .map((sub) => ({
                  label: `${cat.name} / ${sub.name}`,
                  to: `/category/${cat.slug}?sub=${encodeURIComponent(sub.slug)}`,
                }));
              return [...base, ...subLinks];
            })
            .slice(0, 8);

          if (mapped.length) setDynamicCategories(mapped);
        })
        .catch(() => {});
    };

    loadCategories();
    window.addEventListener('focus', loadCategories);
    return () => window.removeEventListener('focus', loadCategories);
  }, []);

  const mergedQuickLinks = useMemo(() => {
    const categoryLinks = dynamicCategories.slice(0, 3);
    return [quickLinks[0], ...categoryLinks, ...quickLinks.slice(1)];
  }, [dynamicCategories]);

  return (
    <footer className="bg-[#040a18] border-t border-white/[0.06] relative overflow-hidden">

      {/* Ambient glow */}
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[400px] rounded-full blur-[120px] pointer-events-none opacity-[0.05]"
        style={{ background: 'radial-gradient(ellipse, #00d4ff 0%, #7c3aed 50%, transparent 80%)' }} />

      {/* ── Promo strip ── */}
      <div className="border-b border-white/[0.05]" style={{ background: 'linear-gradient(90deg, rgba(0,212,255,0.04) 0%, rgba(124,58,237,0.06) 50%, rgba(0,212,255,0.04) 100%)' }}>
        <div className="max-w-[1400px] mx-auto px-[5%] py-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-6 flex-wrap">
            {[
              { icon: '🚚', text: 'Free Shipping over Rs. 2,000' },
              { icon: '🔁', text: '30-Day Easy Returns' },
              { icon: '🔒', text: '100% Secure Payment' },
              { icon: '📞', text: '24/7 Customer Support' },
            ].map(item => (
              <div key={item.text} className="flex items-center gap-1.5">
                <span className="text-sm">{item.icon}</span>
                <span className="text-[#4a5870] text-xs font-medium">{item.text}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#22c55e] animate-pulse flex-shrink-0" />
            <span className="text-[#4a5870] text-xs">All systems operational</span>
          </div>
        </div>
      </div>

      {/* ── Main 4-col grid ── */}
      <div className="max-w-[1400px] mx-auto px-[5%] pt-14 pb-10 grid grid-cols-[2.2fr_1fr_1.4fr_1.4fr] gap-10 max-[1100px]:grid-cols-2 max-[580px]:grid-cols-1">

        {/* Column 1: Brand */}
        <div className="flex flex-col gap-5">
          <Link to="/" className="flex items-center gap-3 group w-fit">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center transition-all group-hover:scale-110"
              style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)' }}>
              <img src={footer_logo} alt="TechZone" className="w-6 brightness-0 invert opacity-80" />
            </div>
            <span className="text-white font-black text-xl tracking-tight group-hover:text-[#00d4ff] transition-colors">⚡TechZone</span>
          </Link>

          <p className="text-[#3d5068] text-[13px] leading-[1.85] max-w-[270px]">
            Pakistan's #1 tech marketplace. Premium gadgets, blazing-fast delivery, and unbeatable prices — all under one roof.
          </p>

          {/* Rating */}
          <div className="flex items-center gap-2 w-fit px-3 py-2 rounded-xl"
            style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.13)' }}>
            <div className="flex gap-0.5">
              {[1,2,3,4,5].map(i => (
                <svg key={i} width="11" height="11" viewBox="0 0 24 24" fill="#fbbf24" stroke="none">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              ))}
            </div>
            <span className="text-[#fbbf24] text-xs font-bold">4.9</span>
            <span className="text-[#3d5068] text-xs">· 12,400+ Reviews</span>
          </div>

          {/* Social */}
          <div>
            <p className="text-[#2e3d52] text-[10px] font-bold tracking-[2.5px] uppercase mb-3">Follow Us</p>
            <div className="flex gap-2.5">
              {socialLinks.map(s => (
                <a key={s.label} href={s.href} title={s.label}
                  className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 hover:-translate-y-1 group"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = `${s.hoverColor}50`; e.currentTarget.style.background = `${s.hoverColor}10`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                >
                  <img src={s.img} alt={s.label} className="w-5 h-5 brightness-0 invert opacity-40 group-hover:opacity-100 transition-opacity" />
                </a>
              ))}
            </div>
          </div>

          {/* Payment badges */}
          <div>
            <p className="text-[#2e3d52] text-[10px] font-bold tracking-[2.5px] uppercase mb-3">We Accept</p>
            <div className="flex flex-wrap gap-2">
              {payments.map(p => (
                <span key={p.label} className="text-[10px] font-bold px-2.5 py-1.5 rounded-lg transition-all hover:scale-105 cursor-default"
                  style={{ background: `${p.color}12`, border: `1px solid ${p.color}28`, color: p.color }}>
                  {p.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Column 2: Quick Links */}
        <div>
          <h4 className="text-white font-extrabold text-[13px] mb-6 flex items-center gap-2.5">
            <span className="w-5 h-[2px] rounded-full bg-gradient-to-r from-[#00d4ff] to-[#7c3aed]" />
            Quick Links
          </h4>
          <ul className="flex flex-col gap-3.5 list-none">
            {mergedQuickLinks.map(l => (
              <li key={l.label}>
                <Link to={l.to}
                  className="text-[#3d5068] text-[13px] hover:text-[#00d4ff] transition-all flex items-center gap-2.5 group hover:translate-x-1">
                  <svg width="5" height="5" viewBox="0 0 5 5" className="flex-shrink-0 fill-[#2a3547] group-hover:fill-[#00d4ff] transition-colors">
                    <circle cx="2.5" cy="2.5" r="2.5"/>
                  </svg>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 3: Categories */}
        <div>
          <h4 className="text-white font-extrabold text-[13px] mb-6 flex items-center gap-2.5">
            <span className="w-5 h-[2px] rounded-full bg-gradient-to-r from-[#7c3aed] to-[#f59e0b]" />
            Shop Categories
          </h4>
          <ul className="flex flex-col gap-3.5 list-none">
            {dynamicCategories.map(l => (
              <li key={l.label}>
                <Link to={l.to}
                  className="text-[#3d5068] text-[13px] hover:text-[#a78bfa] transition-all flex items-center gap-2.5 group hover:translate-x-1">
                  <svg width="5" height="5" viewBox="0 0 5 5" className="flex-shrink-0 fill-[#2a3547] group-hover:fill-[#7c3aed] transition-colors">
                    <circle cx="2.5" cy="2.5" r="2.5"/>
                  </svg>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 4: Contact + Newsletter */}
        <div>
          <h4 className="text-white font-extrabold text-[13px] mb-6 flex items-center gap-2.5">
            <span className="w-5 h-[2px] rounded-full bg-gradient-to-r from-[#f59e0b] to-[#22c55e]" />
            Get In Touch
          </h4>

          <div className="flex flex-col gap-4 mb-8">
            {[
              { icon: '📍', text: 'Gulberg III, Lahore, Pakistan' },
              { icon: '📞', text: '+92 300 1234567' },
              { icon: '✉️', text: 'support@techzone.pk' },
              { icon: '🕐', text: 'Mon–Sat: 9AM – 8PM' },
            ].map(c => (
              <div key={c.text} className="flex items-start gap-3 group">
                <span className="text-base flex-shrink-0 mt-0.5">{c.icon}</span>
                <span className="text-[#3d5068] text-[13px] leading-snug group-hover:text-[#6a80a0] transition-colors">{c.text}</span>
              </div>
            ))}
          </div>

          {/* Mini newsletter */}
          <div className="rounded-2xl p-4" style={{ background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.1)' }}>
            <p className="text-white text-xs font-bold mb-1">📧 Newsletter</p>
            <p className="text-[#3d5068] text-[11px] mb-3 leading-relaxed">Weekly deals &amp; new arrivals in your inbox.</p>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="your@email.com"
                className="flex-1 min-w-0 h-9 rounded-lg px-3 text-[#c0cce0] text-xs outline-none placeholder:text-[#2a3547] transition-all font-[inherit]"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                onFocus={e => e.currentTarget.style.borderColor = 'rgba(0,212,255,0.4)'}
                onBlur={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'}
              />
              <button
                className="flex-shrink-0 h-9 px-4 rounded-lg text-[#060c18] text-sm font-extrabold cursor-pointer border-none transition-all hover:-translate-y-0.5 font-[inherit]"
                style={{ background: 'linear-gradient(135deg, #00d4ff, #0095c8)', boxShadow: '0 4px 16px rgba(0,212,255,0.25)' }}>
                →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Divider ── */}
      <div className="max-w-[1400px] mx-auto px-[5%]">
        <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(0,212,255,0.12), rgba(124,58,237,0.12), transparent)' }} />
      </div>

      {/* ── Bottom bar ── */}
      <div className="max-w-[1400px] mx-auto px-[5%] py-5 flex items-center justify-between flex-wrap gap-3">
        <p className="text-[#243040] text-xs">
          © 2026 <span className="text-[#2e4060]">TechZone</span>. All rights reserved. Made with ❤️ in Pakistan.
        </p>
        <div className="flex items-center gap-5 flex-wrap">
          {policies.map(t => (
            <Link key={t} to="#" className="text-[#243040] text-xs hover:text-[#00d4ff] transition-colors">{t}</Link>
          ))}
        </div>
      </div>
    </footer>
  )
}

export default Footer
